/* ============================================================
   THE VAULT — Motion Engine
   Depends on GSAP + ScrollTrigger + Lenis (loaded in theme.liquid)
   Every animation degrades gracefully w/o JS or w/ reduced-motion.
   ============================================================ */
(function () {
  "use strict";

  const root = document.documentElement;
  root.classList.remove("no-js");
  root.classList.add("js");

  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasGSAP = typeof window.gsap !== "undefined";
  if (hasGSAP && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  /* ----------------------------------------------------------
     1. SMOOTH SCROLL (Lenis) synced to ScrollTrigger
  ---------------------------------------------------------- */
  let lenis = null;
  function initSmoothScroll() {
    if (REDUCED || typeof window.Lenis === "undefined") return;
    lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
    });
    if (hasGSAP) {
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    window.__lenis = lenis;
  }

  /* ----------------------------------------------------------
     2. PAGE LOADER
  ---------------------------------------------------------- */
  function initLoader(done) {
    const loader = document.querySelector("[data-loader]");
    if (!loader || REDUCED || !hasGSAP) {
      if (loader) loader.remove();
      root.classList.add("is-loaded");
      done && done();
      return;
    }
    const countEl = loader.querySelector("[data-loader-count]");
    const barEl = loader.querySelector("[data-loader-bar]");
    const obj = { v: 0 };
    const tl = gsap.timeline({
      onComplete: () => {
        gsap.to(loader, {
          yPercent: -100, duration: 1, ease: "power4.inOut",
          onComplete: () => { loader.remove(); root.classList.add("is-loaded"); done && done(); },
        });
      },
    });
    tl.to(obj, {
      v: 100, duration: 1.6, ease: "power2.inOut",
      onUpdate: () => {
        const n = Math.round(obj.v);
        if (countEl) countEl.textContent = String(n).padStart(2, "0");
        if (barEl) barEl.style.width = n + "%";
      },
    });
  }

  /* ----------------------------------------------------------
     3. CUSTOM CURSOR
  ---------------------------------------------------------- */
  function initCursor() {
    if (window.matchMedia("(pointer:coarse)").matches || REDUCED || !hasGSAP) return;
    const dot = document.querySelector("[data-cursor]");
    const ring = document.querySelector("[data-cursor-ring]");
    if (!dot || !ring) return;
    const xTo = gsap.quickTo(dot, "x", { duration: 0.15, ease: "power3" });
    const yTo = gsap.quickTo(dot, "y", { duration: 0.15, ease: "power3" });
    const rxTo = gsap.quickTo(ring, "x", { duration: 0.5, ease: "power3" });
    const ryTo = gsap.quickTo(ring, "y", { duration: 0.5, ease: "power3" });
    window.addEventListener("mousemove", (e) => {
      xTo(e.clientX); yTo(e.clientY); rxTo(e.clientX); ryTo(e.clientY);
    });
    const hoverTargets = "a,button,[data-cursor-hover],.btn,input,textarea,select";
    document.body.addEventListener("mouseover", (e) => {
      if (e.target.closest(hoverTargets)) ring.classList.add("is-hover");
    });
    document.body.addEventListener("mouseout", (e) => {
      if (e.target.closest(hoverTargets)) ring.classList.remove("is-hover");
    });
  }

  /* ----------------------------------------------------------
     4. SCROLL REVEALS (generic, opt-in via data attributes)
  ---------------------------------------------------------- */
  function initReveals(scope) {
    if (!hasGSAP || REDUCED) {
      (scope || document).querySelectorAll("[data-reveal],[data-reveal-line]").forEach((el) => {
        el.style.opacity = 1; el.style.transform = "none";
        el.querySelectorAll("span").forEach((s) => (s.style.transform = "none"));
      });
      return;
    }
    const ctx = scope || document;

    // fade + rise
    gsap.utils.toArray(ctx.querySelectorAll("[data-reveal]")).forEach((el) => {
      gsap.to(el, {
        opacity: 1, y: 0, duration: 1.1, ease: "power3.out",
        delay: parseFloat(el.dataset.revealDelay || 0),
        scrollTrigger: { trigger: el, start: "top 88%" },
      });
    });

    // line-by-line text
    gsap.utils.toArray(ctx.querySelectorAll("[data-reveal-line]")).forEach((el) => {
      const spans = el.querySelectorAll("span");
      gsap.to(spans, {
        yPercent: 0, duration: 1.1, ease: "power4.out", stagger: 0.08,
        scrollTrigger: { trigger: el, start: "top 85%" },
      });
    });

    // clip mask media
    gsap.utils.toArray(ctx.querySelectorAll(".mask")).forEach((el) => {
      gsap.to(el, {
        clipPath: "inset(0 0 0% 0)", duration: 1.3, ease: "power4.inOut",
        scrollTrigger: { trigger: el, start: "top 82%" },
      });
      const img = el.querySelector("img,video");
      if (img) gsap.fromTo(img, { scale: 1.25 }, {
        scale: 1, duration: 1.6, ease: "power4.out",
        scrollTrigger: { trigger: el, start: "top 82%" },
      });
    });

    // parallax layers
    gsap.utils.toArray(ctx.querySelectorAll("[data-parallax]")).forEach((el) => {
      const amt = parseFloat(el.dataset.parallax || 20);
      gsap.to(el, {
        yPercent: amt, ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true },
      });
    });
  }

  /* ----------------------------------------------------------
     5. ANIMATED NUMBERS
  ---------------------------------------------------------- */
  function initCounters(scope) {
    const els = (scope || document).querySelectorAll("[data-count]");
    els.forEach((el) => {
      const target = parseFloat(el.dataset.count);
      const dec = (el.dataset.countDec | 0);
      const suffix = el.dataset.countSuffix || "";
      if (REDUCED || !hasGSAP) { el.textContent = target.toFixed(dec) + suffix; return; }
      const obj = { v: 0 };
      gsap.to(obj, {
        v: target, duration: 2, ease: "power2.out",
        scrollTrigger: { trigger: el, start: "top 90%", once: true },
        onUpdate: () => { el.textContent = obj.v.toFixed(dec) + suffix; },
      });
    });
  }

  /* ----------------------------------------------------------
     6. INFINITE MARQUEE
  ---------------------------------------------------------- */
  function initMarquee(scope) {
    if (!hasGSAP || REDUCED) return;
    (scope || document).querySelectorAll("[data-marquee]").forEach((el) => {
      const track = el.querySelector(".marquee__track");
      if (!track) return;
      const clone = track.cloneNode(true);
      el.appendChild(clone);
      const speed = parseFloat(el.dataset.marqueeSpeed || 30);
      const dir = el.dataset.marqueeDir === "right" ? 1 : -1;
      gsap.to(el.querySelectorAll(".marquee__track"), {
        xPercent: dir * -100, repeat: -1, duration: speed, ease: "none",
      });
    });
  }

  /* ----------------------------------------------------------
     7. DYNAMIC HEADER + SCROLL PROGRESS + BACK TO TOP
  ---------------------------------------------------------- */
  function initUIChrome() {
    const header = document.querySelector("[data-header]");
    const progress = document.querySelector("[data-scroll-progress]");
    const toTop = document.querySelector("[data-to-top]");
    let last = 0;

    const onScroll = (y) => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      const pct = max > 0 ? y / max : 0;
      if (progress) progress.style.transform = `scaleX(${pct})`;
      if (header) {
        header.classList.toggle("is-scrolled", y > 40);
        header.classList.toggle("is-hidden", y > last && y > 400);
      }
      if (toTop) toTop.classList.toggle("is-visible", y > window.innerHeight);
      last = y;
    };

    if (lenis) lenis.on("scroll", ({ scroll }) => onScroll(scroll));
    else window.addEventListener("scroll", () => onScroll(window.scrollY), { passive: true });

    if (toTop) toTop.addEventListener("click", () => {
      if (lenis) lenis.scrollTo(0, { duration: 1.4 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  /* ----------------------------------------------------------
     8. NAV / SEARCH / THEME TOGGLE
  ---------------------------------------------------------- */
  function initNav() {
    const body = document.body;
    const menuBtn = document.querySelector("[data-menu-toggle]");
    const nav = document.querySelector("[data-nav-overlay]");
    if (menuBtn && nav) {
      menuBtn.addEventListener("click", () => {
        const open = nav.classList.toggle("is-open");
        menuBtn.setAttribute("aria-expanded", open);
        body.classList.toggle("no-scroll", open);
        if (lenis) open ? lenis.stop() : lenis.start();
      });
      nav.querySelectorAll("a").forEach((a) =>
        a.addEventListener("click", () => {
          nav.classList.remove("is-open");
          menuBtn.setAttribute("aria-expanded", false);
          body.classList.remove("no-scroll");
          if (lenis) lenis.start();
        })
      );
    }

    const searchBtn = document.querySelector("[data-search-toggle]");
    const searchOverlay = document.querySelector("[data-search-overlay]");
    if (searchBtn && searchOverlay) {
      const close = () => {
        searchOverlay.classList.remove("is-open");
        body.classList.remove("no-scroll");
        if (lenis) lenis.start();
      };
      searchBtn.addEventListener("click", () => {
        searchOverlay.classList.add("is-open");
        body.classList.add("no-scroll");
        if (lenis) lenis.stop();
        const input = searchOverlay.querySelector("input[type=search]");
        input && setTimeout(() => input.focus(), 300);
      });
      searchOverlay.querySelector("[data-search-close]")?.addEventListener("click", close);
      document.addEventListener("keydown", (e) => e.key === "Escape" && close());
    }

    // theme toggle (dark/light) persisted
    const themeBtn = document.querySelector("[data-theme-toggle]");
    if (themeBtn) {
      themeBtn.addEventListener("click", () => {
        const next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
        root.setAttribute("data-theme", next);
        try { localStorage.setItem("vault-theme", next); } catch (e) {}
      });
    }
  }

  /* ----------------------------------------------------------
     BOOT
  ---------------------------------------------------------- */
  function boot() {
    initSmoothScroll();
    initCursor();
    initNav();
    initUIChrome();
    initLoader(() => {
      initReveals(document);
      initCounters(document);
      initMarquee(document);
      window.__vaultReady = true;
      window.dispatchEvent(new CustomEvent("vault:ready"));
      if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
    });
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot);
  else boot();

  /* ----------------------------------------------------------
     THEME EDITOR — re-init sections on load/select
  ---------------------------------------------------------- */
  document.addEventListener("shopify:section:load", (e) => {
    initReveals(e.target); initCounters(e.target); initMarquee(e.target);
    if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
    window.dispatchEvent(new CustomEvent("vault:section-load", { detail: e }));
  });
  document.addEventListener("shopify:section:select", () => {
    if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // expose for section scripts
  window.Vault = { initReveals, initCounters, initMarquee, get lenis() { return lenis; }, REDUCED, hasGSAP };
})();
